import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useContractData } from './ContractCharts'
import { backendClient } from '../../../api/backend'

const GOLD = '#c9a96e'

const STAGE_CONFIG = [
  { key: 'draft', colorBase: '#78716c', tab: 'draft' },
  { key: 'inReview', colorBase: '#f59e0b', tab: 'in_review' },
  { key: 'signing', colorBase: '#3b82f6', tab: 'pending_signature' },
  { key: 'active', colorBase: '#22c55e', tab: 'signed' },
  { key: 'expiring', colorBase: '#f97316', tab: null },
  { key: 'expired', colorBase: '#ef4444', tab: null },
]

const PipelineFunnel = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const isDark = theme.palette.mode === 'dark'
  const { data, loading: stageLoading } = useContractData()
  const [lifecycleCounts, setLifecycleCounts] = useState({ expiring: 0, expired: 0 })
  const [lifecycleLoading, setLifecycleLoading] = useState(true)

  useEffect(() => {
    async function fetchLifecycle() {
      setLifecycleLoading(true)
      try {
        const response = await backendClient.get('api/contract/monitoring/lifecycle').then((r) => r.json())
        if (Array.isArray(response)) {
          setLifecycleCounts({
            expiring: response.filter((item) => item.lifecycle_status === 'expiring_soon').length,
            expired: response.filter((item) => item.lifecycle_status === 'expired').length,
          })
        }
      } catch (err) {
        console.error('Error fetching lifecycle for pipeline:', err)
      } finally {
        setLifecycleLoading(false)
      }
    }
    fetchLifecycle()
  }, [])

  const stages = data?.stages || {}
  const loading = stageLoading || lifecycleLoading

  const counts = [
    stages.draft?.total ?? 0,
    stages.in_review?.total ?? 0,
    stages.pending_signature?.total ?? 0,
    stages.signed?.total ?? 0,
    lifecycleCounts.expiring,
    lifecycleCounts.expired,
  ]

  const myActions = [
    0,
    stages.in_review?.my_action_count ?? 0,
    stages.pending_signature?.my_action_count ?? 0,
    0,
    0,
    0,
  ]

  const labels = [
    t('dashboard.pipeline.draft', 'Drafts'),
    t('dashboard.pipeline.inReview', 'In Review'),
    t('dashboard.pipeline.signing', 'Signing'),
    t('dashboard.pipeline.active', 'Active'),
    t('dashboard.pipeline.expiring', 'Expiring'),
    t('dashboard.pipeline.expired', 'Expired'),
  ]

  const totalContracts = counts.reduce((sum, c) => sum + c, 0)
  const maxCount = Math.max(...counts, 1)

  const handleClick = (index) => {
    const config = STAGE_CONFIG[index]
    if (config.tab) {
      navigate(`/contract?tab=${config.tab}`)
    } else {
      navigate('/monitoring')
    }
  }

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{ flex: 1, height: 88, borderRadius: '10px' }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow: isDark
          ? '0 2px 8px rgba(0, 0, 0, 0.3)'
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              color: GOLD,
              letterSpacing: '0.02em',
            }}
          >
            {t('dashboard.pipeline.title', 'Contract Pipeline')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
          >
            {totalContracts}{' '}
            {t('clmDashboard.totalContracts', 'total contracts')}
          </Typography>
        </Box>

        {/* Stage cards */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {STAGE_CONFIG.map((stage, index) => {
            const color = stage.colorBase
            const count = counts[index]
            const myAction = myActions[index]
            const label = labels[index]
            const barWidth =
              maxCount > 0 ? Math.max((count / maxCount) * 100, 6) : 6

            return (
              <Box
                key={stage.key}
                onClick={() => handleClick(index)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '10px',
                  p: '14px 12px 12px',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  background: isDark
                    ? alpha(color, 0.05)
                    : alpha(color, 0.04),
                  border: `1px solid ${alpha(color, isDark ? 0.12 : 0.1)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: alpha(color, isDark ? 0.1 : 0.08),
                    borderColor: alpha(color, 0.3),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${alpha(color, isDark ? 0.15 : 0.1)}`,
                  },
                }}
              >
                {/* Label row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: color,
                      flexShrink: 0,
                      boxShadow: `0 0 6px ${alpha(color, 0.4)}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      letterSpacing: '0.02em',
                      lineHeight: 1,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>

                {/* Count */}
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                    mb: myAction > 0 ? 0.5 : 1,
                  }}
                >
                  {count}
                </Typography>

                {/* "for me" badge */}
                {myAction > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color,
                      fontSize: '0.65rem',
                      mb: 1,
                      lineHeight: 1,
                    }}
                  >
                    {myAction} {t('clmDashboard.forMe', 'for me')}
                  </Typography>
                )}

                {/* Mini progress bar */}
                <Box
                  sx={{
                    width: '100%',
                    height: 3,
                    borderRadius: 2,
                    background: alpha(color, isDark ? 0.1 : 0.08),
                    mt: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      width: `${barWidth}%`,
                      height: '100%',
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${alpha(color, 0.6)}, ${color})`,
                      transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </Box>
              </Box>
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}

export default PipelineFunnel
